import { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setApiVersion, setVersionLoading, setVersionError } from '../slicers/VersionSlice';
import VersionService from '../services/VersionService';

/**
 * Custom hook for managing version information with API fallback
 * @returns {Object} Version information and methods
 */
const useVersion = () => {
    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const versionState = useSelector((state) => state.version);
    const tokens = useSelector((state) => state.userInfo.tokens);

    /**
     * Fetch version from API and update Redux state
     */
    const fetchVersion = useCallback(async () => {
        if (!axiosConfig.envURL) {
            return;
        }

        dispatch(setVersionLoading(true));
        
        try {
            const result = await VersionService.getVersionWithFallback(axiosConfig.envURL, tokens);
            
            dispatch(setApiVersion({
                version: result.version,
                source: result.source
            }));
        } catch (error) {
            dispatch(setVersionError(error.message));
        } finally {
            dispatch(setVersionLoading(false));
        }
    }, [axiosConfig.envURL, tokens, dispatch]);

    /**
     * Get the current version to display
     * @returns {string} The version string to display
     */
    const getDisplayVersion = useCallback(() => {
        return versionState.apiVersion || process.env.REACT_APP_VERSION || '1.0.0';
    }, [versionState.apiVersion]);

    /**
     * Get version source information
     * @returns {string} 'api' or 'env'
     */
    const getVersionSource = useCallback(() => {
        return versionState.versionSource || 'env';
    }, [versionState.versionSource]);

    /**
     * Check if version is from API
     * @returns {boolean}
     */
    const isApiVersion = useCallback(() => {
        return getVersionSource() === 'api';
    }, [getVersionSource]);

    /**
     * Refresh version (clear cache and fetch again)
     */
    const refreshVersion = useCallback(() => {
        VersionService.clearCache();
        fetchVersion();
    }, [fetchVersion]);

    // Auto-fetch version when component mounts or envURL changes
    useEffect(() => {
        if (axiosConfig.envURL) {
            fetchVersion();
        }
    }, [fetchVersion, axiosConfig.envURL]);

    // Re-fetch version after authentication (when tokens change)
    useEffect(() => {
        if (axiosConfig.envURL && tokens) {
            fetchVersion();
        }
    }, [tokens, axiosConfig.envURL, fetchVersion]);

    return {
        // Version data
        version: getDisplayVersion(),
        versionSource: getVersionSource(),
        isApiVersion: isApiVersion(),
        isLoading: versionState.isLoading,
        error: versionState.error,
        
        // Methods
        fetchVersion,
        refreshVersion,
        getDisplayVersion,
        getVersionSource
    };
};

export default useVersion;
